import _ from "lodash";
import { Box, Button, ButtonGroup, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import InputBox from "./InputBox";
import { useCallback, useState } from "react";
import ImageBox from "components/basic/ImageBox";
import FormContent from "../FormContent";
import { NestedField, PField, useFieldValue } from "./NestedField";
import { rotateImage, scaleImage } from "util/image";
import ConfirmDialog from "components/dialog/ConfirmDialog";
import { showDialog } from "components/dialog";
import useNotification from "notification";

/**
 * Input para upload de imagens. As imagen são redimensionadas para maxWidth x maxHeight.
 */
export default function ImageInput({
    name,
    label,
    imageField = "base64",
    multiple = false,
    maxWidth = 480,
    maxHeight = 480,
    quality = 0.7,
    format = "image/jpeg",
    small,
    onRemove,
    sx,
    children,
    maxQt,
    ...props
}) {
    const { showWarning } = useNotification()
    const [fileInput, setFileInput] = useState();
    const [deleteDialog, setDeleteDialog] = useState();

    let [files, setFiles] = useFieldValue(name);

    const fileInputRef = useCallback((node) => {
        if (node) {
            setFileInput(node);
        }
    }, []);

    async function onFileSelected(event) {
        if (event.target.files.length > 0) {

            let newFiles = [...files];
            if (maxQt && event.target.files.length + files.length > maxQt) {
                showWarning("Limite de " + maxQt + " arquivos")
                return
            }

            if (multiple) {
                for (let file of event.target.files) {
                    newFiles.push({
                        [imageField]: await scaleImage(file, maxWidth, maxHeight, format, quality),
                    });
                }
            } else {
                newFiles = {
                    ...newFiles[0],
                    [imageField]: await scaleImage(event.target.files[0], maxWidth, maxHeight, format, quality),
                };
            }

            // Update Final Form field.
            setFiles(newFiles);
        }
    }

    function selectFiles() {
        if (fileInput) {
            fileInput.click();
        }
    }

    function remove(index) {
        showDialog(deleteDialog).then((confirm) => {
            if (confirm) {
                let newFiles;
                let removed;

                if (multiple) {
                    newFiles = [...files];
                    removed = newFiles.splice(index, 1)[0];
                } else {
                    removed = files;
                    newFiles = null;
                }

                setFiles(newFiles);

                if (onRemove) {
                    onRemove(removed, index);
                }
            }
        });
    }

    async function rotate(index, degrees) {
        let newFiles;

        if (multiple) {
            newFiles = [...files];
            newFiles[index][imageField] = await rotateImage(newFiles[index][imageField], degrees);
        } else {
            newFiles = { ...files };
            newFiles[imageField] = await rotateImage(newFiles[imageField], degrees);
        }

        setFiles(newFiles);
    }

    let fileArray = [];

    if (_.isArray(files)) {
        fileArray = files;
    } else if (files) {
        fileArray = [files];
    }

    return (
        <InputBox sx={sx}>
            <PField name={name}>
                {(fieldProps) => (
                    <>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}>
                            <Box sx={{ display: 'flex' }}>

                                {label ? <Typography variant="h6" sx={{ flex: 1 }}>{label}</Typography> : null}

                                {/* Oculta botão adicionar em modo pequeno quando já houver uma imagem. */}
                                {small && !multiple && files ? null : (
                                    <Box>
                                        <Button
                                            disabled={maxQt ? files.length >= maxQt : false}
                                            startIcon={small ? null : <AddIcon />}
                                            size={small ? "small" : "medium"}
                                            onClick={selectFiles}
                                            title="Adicionar Imagem"
                                        >
                                            {small ? <AddIcon /> : multiple ? "Adicionar Imagens" : "Selecionar Imagem"}
                                        </Button>
                                    </Box>
                                )}

                            </Box>
                            {fileArray.map((file, index) => (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        justifyContent: 'center',
                                        gap: 2,
                                        pb: multiple ? 2 : 0,
                                        borderBottom: "1px dotted",
                                        borderColor: "primary.main",
                                        "&:last-child": { borderBottom: 0 },
                                    }}
                                    key={index}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: 'center', gap: small ? 0.5 : 2 }}>
                                        {file[imageField] ? (
                                            <ImageBox
                                                sx={{ width: small ? null : { xs: "200px", sm: "200px" } }} 
                                                maxWidth={small ? 120 : 200}
                                                maxHeight={small ? 120 : 200}
                                                src={file[imageField]}
                                                alt={file[imageField].name}
                                            />
                                        ) : (
                                            <Typography
                                                variant="caption"
                                                sx={{ width: { xs: "100%", sm: "200px", textAlign: "center" } }}
                                            >
                                                Imagem não carregada
                                            </Typography>
                                        )}

                                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                                            <ButtonGroup variant="outlined" size={small ? "small" : "medium"}>
                                                <Button onClick={() => rotate(index, -90)}>
                                                    <RotateLeftIcon fontSize={small ? "small" : "medium"} />
                                                </Button>
                                                <Button onClick={() => remove(index)}>
                                                    <DeleteForeverIcon fontSize={small ? "small" : "medium"} />
                                                </Button>
                                                <Button onClick={() => rotate(index, 90)}>
                                                    <RotateRightIcon fontSize={small ? "small" : "medium"} />
                                                </Button>
                                            </ButtonGroup>
                                        </Box>
                                    </Box>

                                    {children ? (
                                        <NestedField prefix={name} index={index}>
                                            <FormContent>{children}</FormContent>
                                        </NestedField>
                                    ) : null}
                                </Box>
                            ))}
                        </Box>

                        <input
                            ref={fileInputRef}
                            hidden
                            type="file"
                            multiple={multiple}
                            onChange={(event) => onFileSelected(event)}
                        />
                    </>
                )}
            </PField>

            <ConfirmDialog title="Remover" setDialog={setDeleteDialog}>
                Deseja realmente remover esta foto?
            </ConfirmDialog>
        </InputBox>
    );
}
